import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import type { FC } from 'react';

import type { IncompleteEventFormComplete } from '../../forms/event/IncompleteEventForm/schema.ts';
import { generateRecurrences } from '../../lib/generateRecurrences.ts';

interface PublishEventsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmPublish: (data: IncompleteEventFormComplete) => void;
  isPublishing: boolean;
  formEventFull: IncompleteEventFormComplete | null;
}

export const PublishEventsModal: FC<PublishEventsModalProps> = ({
  isOpen,
  onClose,
  onConfirmPublish,
  isPublishing,
  formEventFull,
}) => {
  if (!formEventFull) {
    return null;
  }

  const handleCancel = () => {
    onClose();
  };

  const recurringEvents = formEventFull.recurrenceActive
    ? generateRecurrences({
        date: formEventFull.end,
        recurrenceRate: formEventFull.recurrenceRate ?? 'weekly',
        monthRecurrenceType: formEventFull.recurrenceMonthType,
        noOfEvents: formEventFull.recurrenceAmount,
      })
    : null;

  const modalTitle = recurringEvents ? `Publishing ${recurringEvents.length} recurring events` : 'Publishing event';
  const eventName = formEventFull.name;

  return (
    <Modal
      centered
      opened={isOpen}
      size="lg"
      title={
        <Title component="span" order={3}>
          {modalTitle}
        </Title>
      }
      onClose={onClose}
    >
      <Stack gap="xxxs">
        <Text size="xs">
          You are about to publish the event <strong>{eventName}</strong>.
        </Text>
        {recurringEvents && (
          <Text size="xs">
            This includes <strong>{recurringEvents.length} recurring events</strong> starting from{' '}
            <strong>{formEventFull.start.toLocaleDateString()}</strong> to{' '}
            <strong>{recurringEvents.at(-1)?.toLocaleDateString()}</strong>.
          </Text>
        )}
        <Text c="dimmed" size="xs">
          The events will be reviewed after they are published, ensuring all details are correct.
        </Text>
      </Stack>
      <Group gap="xxxs" justify="flex-end" mt="xxs">
        <Button color="gray" radius="xl" size="md" variant="subtle" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="dark"
          loading={isPublishing}
          radius="xl"
          size="md"
          onClick={() => onConfirmPublish(formEventFull)}
        >
          Confirm & Publish
        </Button>
      </Group>
    </Modal>
  );
};
